import { Address, ClientAddressType, NaturalPerson } from '../../../common/types/client';
import { AppRoutes } from '../../../common/constants';
import { BaseClientType, EditClientStepProps } from '../../types';
import { Card, CardContent, Tab, Tabs } from '@mui/material';
import { DEFAULT_ERROR_MESSAGE, DEFAULT_SAVED_MESSAGE } from '../../../common/lib/fulcrumApi';
import { JSX, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { useUpdateNaturalPerson } from '../../../common/components/mutations';
import AddressDetailsStep from './steps/addressDetailsStep';
import BasicInformationStep from './steps/basicInformationStep';
import ClientPageHeaderSummary from '../../components/clientPageHeaderSummary';
import ClientRelatedPartiesStep from '../../components/editClientSteps/clientRelatedPartiesStep';
import clsx from 'clsx';
import Content from '../../../common/components/content';
import Layout from '../../../common/components/layout';
import LayoutLoadingState from '../../../common/components/layout/layoutLoadingState';
import RatingDetailsStep from './steps/ratingDetailsStep';
import StepFooter from '../../../common/components/stepFooter';

const LAST_STEP_INDEX = 3;

/**
 * Natural person edit page
 */
const EditNaturalPersonPage = (): JSX.Element => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [activeTab, setActiveTab] = useState(0);
  const { mutateAsync, isPending: mutationIsLoading } = useUpdateNaturalPerson();
  const { data: client } = useQuery<NaturalPerson>({
    queryKey: [`/NaturalPerson/${id}`],
  });

  const {
    control,
    reset,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<NaturalPerson>({
    mode: 'onChange',
  });
  // Watch roleplayerID field to understand when form default values is populated (we don't show the form before that)
  const watchRoleplayerID = watch('roleplayerID');

  const commitChanges = async (): Promise<void> => {
    await handleSubmit(onSubmit)();
  };

  const onSubmit = async (data: NaturalPerson): Promise<void> => {
    try {
      await mutateAsync(data);
      enqueueSnackbar(DEFAULT_SAVED_MESSAGE, { variant: 'success' });
    } catch (error) {
      enqueueSnackbar(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (error as any).response?.data?.detail ||
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (error as any).response?.data?.title ||
          (error as Error).message ||
          DEFAULT_ERROR_MESSAGE,
        { variant: 'error' },
      );
      throw error;
    }
  };

  const handleSaveAndExit = (): void => {
    commitChanges().then(() => {
      navigate(`${AppRoutes.NaturalPersons}/${id}`);
    });
  };

  // If there are more steps, go to the next one (otherwise save and go to the client page)
  const handleClickNext = async (): Promise<void> => {
    await commitChanges();
    if (Object.values(errors).length > 0) return;
    if (activeTab < LAST_STEP_INDEX) {
      setActiveTab((value) => value + 1);
    } else {
      handleSaveAndExit();
    }
  };

  // Reset form when client object changes
  useEffect(() => {
    if (client) {
      const formValues = { ...client, addresses: client.addresses || [] };
      // Add missing addresses items if it's not present
      if (!formValues.addresses?.find(({ typeID }) => typeID === ClientAddressType.residential)) {
        formValues.addresses?.push({
          city: '',
          id: 0,
          country: '',
          code: '',
          province: '',
          street: '',
          typeID: ClientAddressType.residential,
          typeNote: 'Residential address',
          roleplayerID: client.roleplayerID,
        } as Address);
      }
      if (!formValues.addresses?.find(({ typeID }) => typeID === ClientAddressType.postal)) {
        formValues.addresses?.push({
          id: 0,
          city: '',
          country: '',
          code: '',
          province: '',
          street: '',
          typeID: ClientAddressType.postal,
          typeNote: 'Postal address',
          roleplayerID: client.roleplayerID,
        } as Address);
      }
      // If there are no phones or emails, add an empty one
      if (!formValues.phones || formValues.phones.length === 0) {
        formValues.phones = [{ id: 0, number: '', roleplayerID: client.roleplayerID }];
      }
      if (!formValues.emails || formValues.emails.length === 0) {
        formValues.emails = [{ id: 0, address: '', roleplayerID: client.roleplayerID }];
      }
      reset(formValues);
    }
  }, [client, reset]);

  if (!client || !watchRoleplayerID) {
    return <LayoutLoadingState />;
  }

  const breadcrumbs = [
    { label: 'Your Clients', href: AppRoutes.Clients },
    { label: client.roleplayerName },
  ];

  const stepProps = {
    watch,
    client,
    control,
    setValue,
    commitChanges,
  };

  return (
    <Layout breadcrumbs={breadcrumbs}>
      <Content>
        <ClientPageHeaderSummary client={client} />

        <Tabs
          className="mt-12"
          variant="scrollable"
          scrollButtons="auto"
          value={activeTab}
          onChange={(_, newValue: number): void => setActiveTab(newValue)}
        >
          {/* Disable tabs to prevent jumping between them and allow navigation only by clicking Next button */}
          <Tab disabled label="Basic information" />
          <Tab disabled label="Rating details" />
          <Tab disabled label="Contact and address details" />
          <Tab disabled label="Related parties" />
        </Tabs>
        <Card>
          <CardContent className={clsx(mutationIsLoading && 'animate-pulse')}>
            {activeTab === 0 && <BasicInformationStep {...stepProps} />}
            {activeTab === 1 && <RatingDetailsStep {...stepProps} />}
            {activeTab === 2 && <AddressDetailsStep {...stepProps} />}
            {activeTab === 3 && (
              <ClientRelatedPartiesStep {...(stepProps as EditClientStepProps<BaseClientType>)} />
            )}
          </CardContent>
        </Card>
        <StepFooter
          isLoading={mutationIsLoading}
          onClickBack={activeTab > 0 ? (): void => setActiveTab((value) => value - 1) : undefined}
          onClickNext={handleClickNext}
          nextButtonTitle={activeTab === LAST_STEP_INDEX ? 'Save and close' : 'Next'}
        />
      </Content>
    </Layout>
  );
};

export default EditNaturalPersonPage;
